import {
    BarsOutlined,
    CalculatorOutlined,
    CodeOutlined,
    FormatPainterOutlined,
    FormOutlined,
    NodeIndexOutlined,
    SettingOutlined,
    // ToolOutlined, -- used by the commented-out Operators section below
} from "@ant-design/icons";

import { PARAMETER_COLORS, ParameterCategory, ParameterItem } from "./ParameterPicker";
import systemParametersData from "./systemParameters.json";

// Union type for custom fields that can come from different sources
export type CustomFieldData = App.Data.IntegrationCustomFieldData | App.Data.FormStructureCustomFieldData;

export interface ParameterDataSources {
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

/**
 * Builds nested picker items from dot-separated identifiers.
 * Example: customer.name.surname -> customer -> name -> surname
 */
const buildHierarchicalItems = (
    identifiers: string[],
    paramType: NonNullable<ParameterItem["paramType"]>,
    labelOverrides?: Record<string, string>
): ParameterItem[] => {
    interface TreeNode {
        id: string;
        name: string;
        children: Map<string, TreeNode>;
    }

    const SELF_LEAF_KEY = "__self__";
    const root = new Map<string, TreeNode>();

    identifiers
        .map((identifier) => identifier?.trim())
        .filter((identifier): identifier is string => Boolean(identifier))
        .forEach((identifier) => {
            const segments = identifier
                .split(".")
                .map((segment) => segment.trim())
                .filter(Boolean);
            if (segments.length === 0) return;

            let currentLevel = root;
            let currentPath = "";

            segments.forEach((segment, index) => {
                currentPath = currentPath ? `${currentPath}.${segment}` : segment;
                const isLeaf = index === segments.length - 1;

                const existing = currentLevel.get(segment);
                if (existing) {
                    if (!isLeaf && existing.children.size === 0) {
                        existing.children.set(SELF_LEAF_KEY, {
                            id: existing.id,
                            name: existing.name,
                            children: new Map<string, TreeNode>(),
                        });
                        existing.id = `${paramType}:${currentPath}`;
                        existing.name = labelOverrides?.[currentPath] ?? segment;
                    } else if (isLeaf && existing.children.size > 0 && !existing.children.has(SELF_LEAF_KEY)) {
                        existing.children.set(SELF_LEAF_KEY, {
                            id: identifier,
                            name: labelOverrides?.[identifier] ?? segment,
                            children: new Map<string, TreeNode>(),
                        });
                    }

                    currentLevel = existing.children;
                    return;
                }

                const node: TreeNode = {
                    id: isLeaf ? identifier : `${paramType}:${currentPath}`,
                    name: isLeaf
                        ? (labelOverrides?.[identifier] ?? segment)
                        : (labelOverrides?.[currentPath] ?? segment),
                    children: new Map<string, TreeNode>(),
                };

                currentLevel.set(segment, node);
                currentLevel = node.children;
            });
        });

    const toItems = (level: Map<string, TreeNode>): ParameterItem[] =>
        Array.from(level.values()).map((node) => {
            const children = toItems(node.children);
            return {
                id: node.id,
                name: node.name,
                paramType,
                ...(children.length > 0 ? { children } : {}),
            };
        });

    return toItems(root);
};

/**
 * Converts FormFieldData array to nested ParameterItem array.
 */
const convertFormFieldsToItems = (formFields: App.Data.FormFieldData[]): ParameterItem[] => {
    const labelOverrides: Record<string, string> = {};
    const validFields = formFields.filter((field): field is App.Data.FormFieldData & { system_name: string } =>
        Boolean(field.system_name)
    );

    validFields.forEach((field) => {
        const [rootSegment] = field.system_name.split(".");
        if (rootSegment) {
            labelOverrides[`data_${rootSegment}`] = rootSegment;
        }
    });

    return buildHierarchicalItems(
        validFields.map((field) => `data_${field.system_name}`),
        "form_fields",
        labelOverrides
    );
};

/**
 * Converts custom fields to flat ParameterItem array using field.parameter as both id and label.
 * This means the picker shows the parameter, the tag displays the parameter, and BE receives {parameter} —
 * matching what TagReplacer::fromCustomColumn() actually replaces on the backend.
 */
const convertCustomFieldsToItems = (customFields: CustomFieldData[]): ParameterItem[] =>
    customFields
        .filter((field): field is CustomFieldData & { parameter: string } => Boolean(field.parameter))
        .map((field) => ({ id: field.parameter, name: field.parameter, paramType: "keys" as const }));

/**
 * Creates parameter categories from form fields and custom fields data
 */
export const createParameterCategoriesFromData = (
    sources: ParameterDataSources,
    t: (key: string) => string
): ParameterCategory[] => {
    const categories: ParameterCategory[] = [];

    // Form Fields category
    if (sources.formFields && sources.formFields.length > 0) {
        categories.push({
            id: "form_fields",
            name: t("parameter_picker.categories.form_fields"),
            icon: (
                <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded"
                    style={{ backgroundColor: PARAMETER_COLORS.form_fields }}
                >
                    <FormOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                </span>
            ),
            type: "form_fields",
            count: sources.formFields.length,
            items: convertFormFieldsToItems(sources.formFields),
        });
    }

    // Custom Fields / Keys category
    if (sources.customFields && sources.customFields.length > 0) {
        categories.push({
            id: "keys",
            name: t("parameter_picker.categories.keys"),
            icon: (
                <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded"
                    style={{ backgroundColor: PARAMETER_COLORS.keys }}
                >
                    <CodeOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                </span>
            ),
            type: "keys",
            count: sources.customFields.length,
            items: convertCustomFieldsToItems(sources.customFields),
        });
    }

    return categories;
};

/**
 * Creates the System Parameters category from the static JSON definition
 */
export const createSystemParameterCategory = (t: (key: string) => string): ParameterCategory => {
    const translateOrFallback = (key: string, fallback: string): string => {
        const translated = t(key);
        return translated === key ? fallback : translated;
    };

    const mapItems = (items: (typeof systemParametersData)[number]["children"]): ParameterItem[] =>
        items.map((item) => ({
            id: item.id,
            name: item.name,
            paramType: "system" as const,
        }));

    return {
        id: "system",
        name: t("parameter_picker.categories.system"),
        icon: (
            <span
                className="inline-flex items-center justify-center w-5 h-5 rounded"
                style={{ backgroundColor: PARAMETER_COLORS.system }}
            >
                <SettingOutlined style={{ color: "#ffffff", fontSize: 12 }} />
            </span>
        ),
        type: "system",
        items: systemParametersData.map((group) => ({
            id: group.id,
            name: translateOrFallback(`parameter_picker.system.groups.${group.id}`, group.name),
            paramType: "system" as const,
            children: mapItems(group.children),
        })),
    };
};

// ============================================================================
// Previous Steps category
// ============================================================================

export interface PreviousStepValue {
    id: string;
    name: string;
    children?: PreviousStepValue[];
    /** See ParameterItem.insertAsRawContainer */
    insertAsRawContainer?: boolean;
}

export interface PreviousStep {
    id: string;
    number: number;
    title: string;
    values: PreviousStepValue[];
    /** Node-type color to distinguish step types in the picker */
    color?: string;
    /** NodeType enum value (e.g. NodeType.Set) — used to render the node's icon */
    nodeType?: string;
    /**
     * Marks the synthetic "General" entry: unlike a real step, its values insert as unprefixed
     * tags/raw text (e.g. "{status}", "{parsedBody.}") instead of being scoped to `${step.id}.`,
     * and its label isn't numbered like the real per-step entries.
     */
    isGeneral?: boolean;
}

/**
 * Creates the Previous Steps category from the current CC diagram steps.
 * Pass an empty array while the data source is not yet wired — the tab will
 * only appear in the picker when at least one step is provided.
 */
export const createPreviousStepsCategory = (steps: PreviousStep[], t: (key: string) => string): ParameterCategory => ({
    id: "steps",
    name: t("parameter_picker.categories.steps"),
    icon: (
        <span
            className="inline-flex items-center justify-center w-5 h-5 rounded"
            style={{ backgroundColor: PARAMETER_COLORS.steps }}
        >
            <NodeIndexOutlined style={{ color: "#ffffff", fontSize: 12 }} />
        </span>
    ),
    type: "steps",
    items: steps.map((step) => {
        const inferValueParamType = (id: string): NonNullable<ParameterItem["paramType"]> => {
            if (id.startsWith("data_")) return "form_fields";
            if (id.startsWith("system.")) return "system";
            if (/^_\d+/.test(id)) return "rows";
            if (
                id.startsWith("parsedBody.") ||
                id.startsWith("query.") ||
                id.startsWith("headers.") ||
                id === "status" ||
                id === "parsedBody" ||
                id === "query" ||
                id === "headers"
            )
                return "http_steps";
            return "steps";
        };
        const mapValue = (v: PreviousStepValue): ParameterItem => {
            const itemId = `${step.id}:${v.id}`;
            const isLeaf = !v.children?.length;
            const decodedId = v.id.replace(/%2E/g, ".");
            return {
                id: itemId,
                name: v.name,
                paramType: inferValueParamType(v.id),
                ...(step.color ? { color: step.color } : {}),
                ...(step.nodeType ? { nodeType: step.nodeType } : {}),
                ...(v.insertAsRawContainer ? { insertAsRawContainer: true } : {}),
                ...(isLeaf
                    ? {
                          value: step.isGeneral ? v.id : `${step.id}.${v.id}`,
                          displayLabel: step.isGeneral
                              ? `${step.title} · ${decodedId}`
                              : `${step.number} - ${step.title} · ${decodedId}`,
                      }
                    : { children: (v.children ?? []).map(mapValue) }),
            };
        };
        return {
            id: step.id,
            name: step.isGeneral ? step.title : `${step.number} - ${step.title}`,
            paramType: "steps" as const,
            ...(step.color ? { color: step.color } : {}),
            ...(step.nodeType ? { nodeType: step.nodeType } : {}),
            children: [...step.values].sort((a, b) => a.name.localeCompare(b.name)).map(mapValue),
        };
    }),
});

// ============================================================================
// Rows category
// ============================================================================

export interface RowsConfig {
    totalRows: number;
    rowLabel: (rowNumber: number) => string;
    currentRowIndex?: number;
    onlyPreviousRows?: boolean;
}

/**
 * Creates the Rows category for pickers that operate within a row context.
 * Returns null if totalRows <= 0 or all rows are filtered out.
 */
export const createRowsCategory = (config: RowsConfig, t: (key: string) => string): ParameterCategory | null => {
    const { totalRows, rowLabel, currentRowIndex, onlyPreviousRows = false } = config;
    if (totalRows <= 0) return null;

    const items = Array.from({ length: totalRows }, (_, i) => {
        const rowNumber = i + 1;
        return {
            id: `_${rowNumber}`,
            value: `_${rowNumber}`,
            name: rowLabel(rowNumber),
            displayLabel: rowLabel(rowNumber),
            paramType: "rows" as const,
        };
    }).filter((item) => {
        if (!onlyPreviousRows || currentRowIndex === undefined) return true;
        const rowNum = parseInt(item.id.replace("_", ""), 10);
        return rowNum < currentRowIndex + 1;
    });

    if (items.length === 0) return null;

    return {
        id: "rows",
        name: t("parameter_picker.categories.rows"),
        icon: (
            <span
                className="inline-flex items-center justify-center w-5 h-5 rounded"
                style={{ backgroundColor: PARAMETER_COLORS.rows }}
            >
                <BarsOutlined style={{ color: "#ffffff", fontSize: 12 }} />
            </span>
        ),
        type: "rows",
        count: items.length,
        items,
    };
};

// ============================================================================
// Functions categories (UI scaffolding — no items until the backend exists)
// ============================================================================

const functionCategoryIcon = (color: string, Icon: typeof BarsOutlined) => (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded" style={{ backgroundColor: color }}>
        <Icon style={{ color: "#ffffff", fontSize: 12 }} />
    </span>
);

const FORMAT_CONVERT_FUNCTIONS = ["toBool", "toFloat", "toInt", "toString"];
const MATH_FUNCTIONS = ["rand", "round", "min", "max", "sum"];
// const OPERATORS = ["+", "-", "<", ">", "<=", ">=", "=", "!=", "/", "*", "mod"]; -- used by the commented-out Operators section below

const toFunctionItems = (names: string[], paramType: NonNullable<ParameterItem["paramType"]>): ParameterItem[] =>
    names.map((name) => ({ id: name, name, paramType }));

/**
 * Creates the Functions subsections (Format & convert, Math Functions, Operators).
 * Items are static placeholders — selecting one inserts its tag, but the backend does not evaluate them yet.
 */
export const createFunctionCategories = (t: (key: string) => string): ParameterCategory[] => [
    {
        id: "format_convert",
        name: t("parameter_picker.categories.format_convert"),
        icon: functionCategoryIcon(PARAMETER_COLORS.format_convert, FormatPainterOutlined),
        type: "format_convert",
        items: toFunctionItems(FORMAT_CONVERT_FUNCTIONS, "format_convert"),
    },
    {
        id: "math_functions",
        name: t("parameter_picker.categories.math_functions"),
        icon: functionCategoryIcon(PARAMETER_COLORS.math_functions, CalculatorOutlined),
        type: "math_functions",
        items: toFunctionItems(MATH_FUNCTIONS, "math_functions"),
    },
    // Operators section hidden for now — not yet in scope, kept in code for later.
    // {
    //     id: "operators",
    //     name: t("parameter_picker.categories.operators"),
    //     icon: functionCategoryIcon(PARAMETER_COLORS.operators, ToolOutlined),
    //     type: "operators",
    //     items: toFunctionItems(OPERATORS, "operators"),
    // },
];
